import { call, put, select, takeLatest } from 'redux-saga/effects'
import {
  setLoading,
  unsetLoading,
} from 'portal-modules/common/redux/modules/loading'
import { createSelector } from 'reselect'
import { getProjects } from '../../api'
import { transformProject } from '../../utils/transform-project'
import { LOADING_KEYS } from '../../constants/display-content'

// Action Types
const PREFIX = 'tenantImprovements'
export const FETCH_PROJECTS_REQUEST = `${PREFIX}/FETCH_PROJECTS_REQUEST`
export const FETCH_PROJECTS_SUCCESS = `${PREFIX}/FETCH_PROJECTS_SUCCESS`
export const FETCH_PROJECTS_FAILURE = `${PREFIX}/FETCH_PROJECTS_FAILURE`

// Action Creators
export const fetchProjectsRequest = () => ({
  type: FETCH_PROJECTS_REQUEST,
})

export const fetchProjectsSuccess = projects => ({
  type: FETCH_PROJECTS_SUCCESS,
  payload: projects,
})

export const fetchProjectsFailure = error => ({
  type: FETCH_PROJECTS_FAILURE,
  payload: error,
})

// Reducer
const initialState = {
  projects: [],
  isLoading: false,
  error: null,
  // True after the first FETCH_PROJECTS_SUCCESS / FAILURE. Used by
  // ProjectDetail to distinguish "we haven't tried to load yet" (during a
  // hard refresh on the detail URL) from "we tried and there is no such
  // project" (so we can show the not-found copy).
  hasFetched: false,
}

export default (state = initialState, action) => {
  switch (action.type) {
    case FETCH_PROJECTS_REQUEST:
      return { ...state, isLoading: true, error: null }
    case FETCH_PROJECTS_SUCCESS:
      return {
        ...state,
        projects: action.payload,
        isLoading: false,
        error: null,
        hasFetched: true,
      }
    case FETCH_PROJECTS_FAILURE:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
        hasFetched: true,
      }
    default:
      return state
  }
}

// Sagas
//
// A "suite" in the tenant portal is a lease; the selected suite's lease id
// lives on the global user slice (populated at login / suite selection).
export const selectUserLeaseId = state => state?.user?.leaseId

export function* fetchProjectsSaga() {
  yield put(
    setLoading({
      key: LOADING_KEYS.TENANT_IMPROVEMENTS,
      message: __('Loading projects…'),
    }),
  )
  try {
    const suiteId = yield select(selectUserLeaseId)
    if (suiteId == null || suiteId === '') {
      // No selected suite — fall through to the shared failure path below
      // so the page shows the standard error state instead of fetching
      // every project for the client.
      throw new Error('Missing suite (lease) id for projects fetch')
    }
    const response = yield call(getProjects, suiteId)
    const rawProjects = response.data || []
    const projects = rawProjects.map(p => transformProject(p))
    yield put(fetchProjectsSuccess(projects))
  } catch (error) {
    // Never propagate raw error.message to user-facing Redux state: backend
    // / transport errors can leak internal paths, service names, CORS
    // details, etc. Log the raw error in non-production for debugging, and
    // put a fixed, translated message in state.
    if (process.env.NODE_ENV !== 'production') {
      console.error('fetchProjectsSaga failed', error)
    }
    yield put(fetchProjectsFailure(__('Failed to load projects')))
  } finally {
    yield put(unsetLoading({ key: LOADING_KEYS.TENANT_IMPROVEMENTS }))
  }
}

function* watchFetchProjects() {
  yield takeLatest(FETCH_PROJECTS_REQUEST, fetchProjectsSaga)
}

export const sagas = [watchFetchProjects]

// Selectors
//
// Lazy memoized id → project map. The reducer stores `projects` as an
// array (preserves order for the list view) but the detail view performs
// a hot lookup by id — previously an O(n) `Array.find` per render per
// useSelector call. createSelector rebuilds the map only when the
// projects array reference changes (i.e., after a successful
// FETCH_PROJECTS_SUCCESS), so steady-state lookups are O(1) and
// allocation-free. Memoization lives on the selector instance instead
// of in module-level state, so independent stores (per-request SSR,
// custom mockStoreCreator instances, etc.) cannot share or stomp on
// each other's cached map.
const selectProjectsArray = state => state?.tenantImprovementProjects?.projects

const selectProjectsById = createSelector([selectProjectsArray], projects => {
  if (!Array.isArray(projects)) return null
  return projects.reduce((acc, p) => {
    if (p && p.id != null) acc[String(p.id)] = p
    return acc
  }, {})
})

export const selectProjectById = id => state => {
  const byId = selectProjectsById(state)
  return byId ? byId[String(id)] : undefined
}

// True iff we have not yet attempted (or are not currently attempting) to
// load the projects list. Used by ProjectDetail on direct URL load /
// refresh, where the user lands on the detail route without ever passing
// through the list view that owns the fetch trigger.
export const selectShouldFetchProjects = state => {
  const slice = state?.tenantImprovementProjects
  if (!slice) return true
  return !slice.isLoading && !slice.hasFetched
}

// True while a fetch is in flight or has not yet been initiated. Used by
// ProjectDetail to decide whether to render the "not found" copy or to
// keep waiting for the list to resolve.
export const selectAreProjectsResolving = state => {
  const slice = state?.tenantImprovementProjects
  if (!slice) return true
  return slice.isLoading || !slice.hasFetched
}
